import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useDashboardStore } from '../stores/dashboardStore';
import './Projects.css';

const Projects: React.FC = () => {
    const { config } = useDashboardStore();
    const { projectId } = useParams<{ projectId: string }>();

    // Scroll to top on page load
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [projectId]);

    // If project ID is specified, show single project details
    if (projectId) {
        const project = config.projects.find(p => p.id === projectId);
        if (!project) {
            return (
                <div className="projects-page">
                    <div className="container">
                        <div className="not-found">
                            <h1>Project Not Found</h1>
                            <p>Sorry, the project you are accessing does not exist.</p>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="project-detail-page">
                <div className="container">
                    <div className="project-header">
                        <h1>{project.title}</h1>
                    </div>

                    <div className="project-content">
                        <div className="project-image">
                            <img src={project.image} alt={project.title} />
                        </div>

                        <div className="project-info">
                            <div className="info-section">
                                <h3>Project Overview</h3>
                                <p>{project.description}</p>
                            </div>

                            <div className="info-section">
                                <h3>Key Features</h3>
                                <ul className="features-list">
                                    {project.features.map((feature, index) => (
                                        <li key={index}>{feature}</li>
                                    ))}
                                </ul>
                            </div>

                            <div className="project-links">
                                {/* {project.demoUrl && (
                                    <a
                                        href={project.demoUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-primary"
                                    >
                                        View Source Code
                                    </a>
                                )} */}
                                {project.githubUrl && (
                                    <a
                                        href={project.githubUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-secondary"
                                    >
                                        View Source Code
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="project-implementation-section">
                        <div className="info-section">
                            <h3>Implementation Path</h3>
                            <div className="implementation-path-box">
                                <ReactMarkdown>{project.implementationPath}</ReactMarkdown>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Display all projects list
    return (
        <div className="projects-page">
            <div className="container">
                <div className="page-header">
                    <h1>Personal Projects</h1>
                    <p>The following projects demonstrate my technical abilities and product thinking, each reflecting a high degree of alignment with the product manager role.</p>
                </div>

                <div className="projects-grid">
                    {config.projects.map((project) => (
                        <div key={project.id} className="project-card">
                            <div className="project-image">
                                <img src={project.image} alt={project.title} />
                            </div>

                            <div className="project-content">
                                <h3>{project.title}</h3>
                                <p>{project.description}</p>

                                <div className="project-features">
                                    <h4>Key Features:</h4>
                                    <ul>
                                        {project.features.slice(0, 3).map((feature, index) => (
                                            <li key={index}>{feature}</li>
                                        ))}
                                        {project.features.length > 3 && (
                                            <li>...</li>
                                        )}
                                    </ul>
                                </div>

                                <div className="project-implementation-path">
                                    <h4>Implementation Path:</h4>
                                    <ReactMarkdown>{project.implementationPath}</ReactMarkdown>
                                </div>

                                <div className="project-actions">
                                    <Link
                                        to={`/projects/${project.id}`}
                                        className="btn btn-primary"
                                    >
                                        View Details
                                    </Link>
                                    {project.demoUrl && (
                                        <a
                                            href={project.demoUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-secondary"
                                        >
                                            View Source Code
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Projects;

